import { z } from "zod";
import { Id } from "./common.js";

/** Home dashboard (spec12): boards (tabs) holding widgets. */

export const MAX_HOME_BOARDS = 20;
export const MAX_BOARD_WIDGETS = 100;

/** FR11: the Lucide icons a link can use (kebab-case names, as Lucide names its files). */
export const HOME_ICONS = [
  "globe",
  "link",
  "house",
  "server",
  "database",
  "hard-drive",
  "cloud",
  "cloud-download",
  "router",
  "wifi",
  "network",
  "shield",
  "shield-check",
  "lock",
  "key-round",
  "container",
  "box",
  "boxes",
  "cpu",
  "memory-stick",
  "monitor",
  "laptop",
  "smartphone",
  "tv",
  "film",
  "clapperboard",
  "music",
  "headphones",
  "radio",
  "podcast",
  "image",
  "camera",
  "book-open",
  "library",
  "newspaper",
  "rss",
  "mail",
  "message-square",
  "calendar-days",
  "square-check-big",
  "notebook-pen",
  "file-text",
  "folder",
  "download",
  "upload",
  "search",
  "chart-line",
  "activity",
  "gauge",
  "terminal",
  "code-xml",
  "git-branch",
  "wrench",
  "settings",
  "bot",
  "lightbulb",
  "thermometer",
  "zap",
  "printer",
  "gamepad-2",
  "shopping-cart",
  "wallet",
  "bell",
  "star",
  "heart",
  "bookmark",
  "users",
  "user",
  "plane",
  "car",
  "map",
  "utensils",
] as const;
export const HomeIconName = z.enum(HOME_ICONS);
export type HomeIconName = z.infer<typeof HomeIconName>;

/** FR7: only absolute http(s) URLs; the scheme is checked after parsing. */
export function isWebUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

export const LinkUrl = z
  .string()
  .trim()
  .max(2048)
  .refine(isWebUrl, { message: "Use an address starting with http:// or https://" });

export const LinkIcon = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("lucide"), name: HomeIconName }),
  /** An uploaded image (spec05 attachment, inline image types only). */
  z.strictObject({ kind: z.literal("image"), attachmentId: Id }),
]);
export type LinkIcon = z.infer<typeof LinkIcon>;

/** FR7: one link is one widget: icon and name. */
export const LinkConfig = z.strictObject({
  name: z.string().trim().min(1).max(60),
  url: LinkUrl,
  icon: LinkIcon,
});
export type LinkConfig = z.infer<typeof LinkConfig>;

export const ClockConfig = z.strictObject({
  seconds: z.boolean(),
  date: z.boolean(),
});
export type ClockConfig = z.infer<typeof ClockConfig>;

/** A text heading over a group of tiles. */
export const HeadingConfig = z.strictObject({
  text: z.string().trim().min(1).max(80),
});
export type HeadingConfig = z.infer<typeof HeadingConfig>;

/** A dashed line across the grid; no settings. */
export const DividerConfig = z.strictObject({});
export type DividerConfig = z.infer<typeof DividerConfig>;

/** The user's boards as boxes side by side, to switch between them; its tile sets the height. */
export const BoardsConfig = z.strictObject({});
export type BoardsConfig = z.infer<typeof BoardsConfig>;

/** spec14: Docker's container name rules; the tile is bound to the name (FR4). */
export const ContainerName = z.string().regex(/^[a-zA-Z0-9][a-zA-Z0-9_.-]{0,127}$/);

/** spec14 FR6a: the info boxes a container card can show, in this order. */
export const ContainerBox = z.enum([
  "uptime",
  "health",
  "version",
  "created",
  "ports",
  "cpu",
  "memory",
  "network",
  "disk",
]);
export type ContainerBox = z.infer<typeof ContainerBox>;
export const DEFAULT_CONTAINER_BOXES: ContainerBox[] = ["uptime", "health", "version"];
/** FR7a: the boxes that need the container's stats. */
export const STATS_BOXES: readonly ContainerBox[] = ["cpu", "memory", "network", "disk"];

/** spec14 FR5: a container tile, like a link; the address is optional. */
export const ContainerConfig = z.strictObject({
  container: ContainerName,
  name: z.string().trim().min(1).max(60),
  url: z.union([z.literal(""), LinkUrl]),
  icon: LinkIcon,
  /** FR6a: missing = Uptime, Health and Version. */
  show: z
    .array(ContainerBox)
    .max(ContainerBox.options.length)
    .refine((boxes) => new Set(boxes).size === boxes.length, { message: "Each box once" })
    .optional(),
});
export type ContainerConfig = z.infer<typeof ContainerConfig>;

/**
 * spec14 FR8: which stats the server widget shows. Missing keys keep the default: CPU, memory
 * and every disk on, network and uptime off.
 */
export const ServerConfig = z.strictObject({
  cpu: z.boolean().optional(),
  memory: z.boolean().optional(),
  /** Disk labels (HOST_DISKS) to show; missing = every disk. */
  disks: z.array(z.string().min(1).max(40)).max(20).optional(),
  network: z.boolean().optional(),
  uptime: z.boolean().optional(),
});
export type ServerConfig = z.infer<typeof ServerConfig>;

export const WidgetType = z.enum([
  "link",
  "clock",
  "heading",
  "divider",
  "boards",
  "container",
  "server",
]);
export type WidgetType = z.infer<typeof WidgetType>;

/**
 * spec12 FR9: the grid's columns (the same as `HOME_GRID_COLUMNS` in @ronin/core, which the
 * layout code uses); heights are in rows of 0.5rem (gap 0.5rem: 16px steps).
 */
const GRID_COLUMNS = 12;

interface Dims {
  w: number;
  h: number;
}

/**
 * FR9 / ADR-0013: every tile has its own width (columns) and height (rows), within its type's
 * limits; new tiles start at the default. 4 rows are 3.5rem, a link's height.
 */
export const WIDGET_LIMITS: Record<WidgetType, { default: Dims; min: Dims; max: Dims }> = {
  link: { default: { w: 2, h: 4 }, min: { w: 1, h: 3 }, max: { w: 12, h: 8 } },
  clock: { default: { w: 2, h: 2 }, min: { w: 1, h: 2 }, max: { w: 6, h: 8 } },
  heading: { default: { w: 6, h: 4 }, min: { w: 1, h: 2 }, max: { w: 12, h: 8 } },
  divider: { default: { w: 12, h: 2 }, min: { w: 1, h: 1 }, max: { w: 12, h: 2 } },
  boards: { default: { w: 12, h: 4 }, min: { w: 2, h: 2 }, max: { w: 12, h: 8 } },
  container: { default: { w: 4, h: 8 }, min: { w: 2, h: 3 }, max: { w: 12, h: 12 } },
  server: { default: { w: 4, h: 4 }, min: { w: 2, h: 2 }, max: { w: 12, h: 16 } },
};

/** `w` x `h` kept within the type's limits. */
export function clampDims(type: WidgetType, dims: Dims): Dims {
  const { min, max } = WIDGET_LIMITS[type];
  return {
    w: Math.min(max.w, Math.max(min.w, Math.round(dims.w))),
    h: Math.min(max.h, Math.max(min.h, Math.round(dims.h))),
  };
}

export const fitsLimits = (type: WidgetType, dims: Dims) => {
  const clamped = clampDims(type, dims);
  return clamped.w === dims.w && clamped.h === dims.h;
};

/**
 * FR9a: the top grid is shared by every board (stored on the main board); the board grid
 * below it belongs to one board.
 */
export const WidgetArea = z.enum(["top", "board"]);
export type WidgetArea = z.infer<typeof WidgetArea>;

/** FR9: the tile's top-left cell on the 12-column grid. */
const Cell = z.number().int().min(0).max(399);
/** FR9: width in columns, height in rows (the type's limits are checked by the API). */
const Width = z.number().int().min(1).max(GRID_COLUMNS);
const Height = z.number().int().min(1).max(24);

/** FR11b: only the content shows, without the tile's background and border. */
const HideBackground = z.boolean();

const widgetBase = {
  id: Id,
  boardId: Id,
  area: WidgetArea,
  x: Cell,
  y: Cell,
  w: Width,
  h: Height,
  hideBackground: HideBackground,
};

export const HomeWidget = z.discriminatedUnion("type", [
  z.object({ ...widgetBase, type: z.literal("link"), config: LinkConfig }),
  z.object({ ...widgetBase, type: z.literal("clock"), config: ClockConfig }),
  z.object({ ...widgetBase, type: z.literal("heading"), config: HeadingConfig }),
  z.object({ ...widgetBase, type: z.literal("divider"), config: DividerConfig }),
  z.object({ ...widgetBase, type: z.literal("boards"), config: BoardsConfig }),
  z.object({ ...widgetBase, type: z.literal("container"), config: ContainerConfig }),
  z.object({ ...widgetBase, type: z.literal("server"), config: ServerConfig }),
]);
export type HomeWidget = z.infer<typeof HomeWidget>;

/**
 * Without `x`/`y` the server puts the tile on the first free spot; without `w`/`h` it gets its
 * type's default size; `area` defaults to board.
 */
const place = {
  x: Cell.optional(),
  y: Cell.optional(),
  w: Width.optional(),
  h: Height.optional(),
  hideBackground: HideBackground.optional(),
  area: WidgetArea.optional(),
};

export const CreateWidget = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("link"), config: LinkConfig, ...place }),
  z.strictObject({ type: z.literal("clock"), config: ClockConfig, ...place }),
  z.strictObject({ type: z.literal("heading"), config: HeadingConfig, ...place }),
  z.strictObject({ type: z.literal("divider"), config: DividerConfig, ...place }),
  z.strictObject({
    type: z.literal("container"),
    config: ContainerConfig,
    ...place,
  }),
  z.strictObject({ type: z.literal("server"), config: ServerConfig, ...place }),
  // The Boards widget is built in (FR6): it cannot be added.
]);
export type CreateWidget = z.infer<typeof CreateWidget>;

/**
 * The type cannot change; the server checks `config` against the widget's type. A widget
 * that changes area goes to the first free spot there when its cell is taken.
 */
export const UpdateWidget = z
  .strictObject({
    x: Cell,
    y: Cell,
    w: Width,
    h: Height,
    hideBackground: HideBackground,
    /** FR9a: move to the top grid, or to the board grid of `boardId`. */
    area: WidgetArea,
    boardId: Id,
    config: z.union([
      LinkConfig,
      ClockConfig,
      HeadingConfig,
      DividerConfig,
      BoardsConfig,
      ContainerConfig,
      ServerConfig,
    ]),
  })
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, { message: "Nothing to update" });
export type UpdateWidget = z.infer<typeof UpdateWidget>;

export const BoardName = z.string().trim().min(1).max(40);

export const HomeBoard = z.object({
  id: Id,
  name: z.string(),
  /** FR3: the main board always exists and holds the top grid. */
  main: z.boolean(),
  /** The board grid's widgets. */
  widgets: z.array(HomeWidget),
});
export type HomeBoard = z.infer<typeof HomeBoard>;

/** spec14 FR3: which optional widgets this server is set up for. */
export const HomeFeatures = z.object({ docker: z.boolean(), serverStats: z.boolean() });
export type HomeFeatures = z.infer<typeof HomeFeatures>;

export const HomeDashboard = z.object({
  boards: z.array(HomeBoard),
  /** FR9a: the top grid, shown on every board. */
  top: z.array(HomeWidget),
  features: HomeFeatures,
});
export type HomeDashboard = z.infer<typeof HomeDashboard>;

export const BoardInput = z.strictObject({ name: BoardName });
export type BoardInput = z.infer<typeof BoardInput>;

/** The full new order of the boards. */
export const OrderInput = z.strictObject({ ids: z.array(Id).max(MAX_BOARD_WIDGETS) });
export type OrderInput = z.infer<typeof OrderInput>;

export const HomeParams = z.object({ id: Id });

/**
 * FR11: service icons from the dashboard-icons set (homarr-labs, Apache-2.0), pinned to one
 * commit on jsDelivr (ADR-0011). The CSP allows images from its `webp/` folder only.
 */
export const ICON_SET_URL =
  "https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons@57e939e504eda0ea764098015da93aa666ad6f31";
export const ICON_SET_IMAGES = `${ICON_SET_URL}/webp/`;

/** FR11: service icons from the dashboard-icons set (ADR-0011). */
export const IconSearchQuery = z.object({ q: z.string().trim().min(1).max(60) });
export const IconSearchResults = z.object({ icons: z.array(z.string()) });
export type IconSearchResults = z.infer<typeof IconSearchResults>;
export const IconParams = z.object({ name: z.string().regex(/^[a-z0-9][a-z0-9-]{0,99}$/) });

/** spec14: Docker's container states and health (`none` without a health check). */
export const ContainerState = z.enum([
  "created",
  "running",
  "paused",
  "restarting",
  "removing",
  "exited",
  "dead",
]);
export type ContainerState = z.infer<typeof ContainerState>;
export const ContainerHealth = z.enum(["none", "starting", "healthy", "unhealthy"]);
export type ContainerHealth = z.infer<typeof ContainerHealth>;

/** spec14: only these fields leave the API; the strict schema drops everything else. */
export const HomeContainer = z.strictObject({
  name: z.string(),
  image: z.string(),
  state: ContainerState,
  health: ContainerHealth,
  /** Docker's short text, e.g. "Up 3 days (healthy)". */
  status: z.string(),
  /** Unix seconds. */
  created: z.number().int().min(0),
  /** FR6a: published host ports, sorted, each once. */
  ports: z.array(z.number().int().min(1).max(65535)),
});
export type HomeContainer = z.infer<typeof HomeContainer>;

export const HomeContainers = z.object({ containers: z.array(HomeContainer) });
export type HomeContainers = z.infer<typeof HomeContainers>;

/**
 * spec14 FR8: memory and disks in bytes, CPU in % (0 to 100), uptime in seconds, network in
 * bytes per second (null when the host's counters are not mounted).
 */
export const ServerStats = z.object({
  cpu: z.number().min(0).max(100),
  cores: z.number().int().min(0),
  memory: z.object({ used: z.number(), total: z.number() }),
  uptime: z.number(),
  network: z.object({ rx: z.number(), tx: z.number() }).nullable(),
  disks: z.array(z.object({ label: z.string(), used: z.number(), total: z.number() })),
});
export type ServerStats = z.infer<typeof ServerStats>;

/** spec14 FR7a: stats of these containers (the cards with resource boxes). */
export const ContainerStatsQuery = z.object({
  names: z
    .string()
    .max(50 * 129)
    .transform((value) => [...new Set(value.split(",").filter(Boolean))])
    .pipe(z.array(ContainerName).min(1).max(50)),
});

/** FR7a: CPU like `docker stats` (100 = one core), bytes and bytes per second. */
export const ContainerStats = z.strictObject({
  name: z.string(),
  cpu: z.number().min(0),
  memory: z.object({ used: z.number().min(0), limit: z.number().min(0) }),
  network: z.object({ rx: z.number().min(0), tx: z.number().min(0) }),
  disk: z.object({ read: z.number().min(0), write: z.number().min(0) }),
});
export type ContainerStats = z.infer<typeof ContainerStats>;
export const HomeContainerStats = z.object({ stats: z.array(ContainerStats) });
export type HomeContainerStats = z.infer<typeof HomeContainerStats>;
